/**
 * ── CÓMO SE LLAMA LA PERSONA QUE APARECE EN LA FOTO ──────────────────────────
 *
 * En toda la app se llamaba "modelo": el modo, la solapa de Mi marca, el picker, los
 * paneles, el chat. La palabra viene del rubro que estrenó el producto —indumentaria,
 * donde "modelo" es el oficio— y en los demás rubros no se entiende igual: una marca de
 * gastronomía o de servicios no contrata modelos.
 *
 * La palabra sigue al RUBRO de la marca activa
 * (ADR-2026-09-21-los-modelos-se-llaman-personajes-o-avatares, corregido por
 * ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo):
 *
 *   • Moda e Indumentaria → **Modelo** / **Modelos**
 *   • Todos los demás     → **Avatar** / **Avatares**
 *
 * En moda NO es "Personaje": ahí "modelo" es el oficio y es la palabra que la marca
 * usa todos los días; "personaje" sonaba a ficción y describía otra cosa.
 *
 * **El nombre del MODO no es esta palabra**: la card del modo, su ítem de la sidebar,
 * su breadcrumb y su nodo de Flujos se llaman **"Modelo"** en TODOS los rubros
 * (`MODO_MODELO`, más abajo). Son dos decisiones distintas —cómo se llama el producto
 * contra cómo se llama la persona— y por eso son dos constantes y no un alias.
 *
 * **Lo que NO cambia y no tiene que cambiar**: el `kind` `design-modelo`, la ruta
 * `/interfaz-v2/modo/modelo`, la tabla `modelos` y su bucket de Storage, el `model_id`
 * que viaja en las tools del agente y el `modo_utilizado` con el que ya se guardaron
 * miles de generaciones. Esto es una palabra de PANTALLA; el contrato de datos sigue
 * diciendo "modelo" y no se toca.
 *
 * **Los dos géneros son masculinos** ("el modelo", "el avatar"), así que el copy que
 * decía "la modelo" / "una modelo" se escribe en masculino. Es la razón por la que acá
 * viven las cuatro formas y no una sola con `+ 's'`: el plural de avatar es
 * "avatares", y quien escribe copy tiene que poder tomar la forma exacta sin pensarla.
 */
import { useSyncExternalStore } from 'react';
import { esRubroModa } from './rubro';
import { rubroMarcaCacheado } from '../hooks/useRubroMarca';

export interface PalabraDeModelo {
  /** "Modelo" / "Avatar" — para títulos, etiquetas de campo y botones. */
  titulo: string;
  /** "Modelos" / "Avatares" — solapas, galerías, contadores. */
  tituloPlural: string;
  /** "modelo" / "avatar" — adentro de una frase. */
  singular: string;
  /** "modelos" / "avatares" — adentro de una frase. */
  plural: string;
}

/** La palabra de Moda e Indumentaria, donde "modelo" es el oficio. */
export const MODELO: PalabraDeModelo = {
  titulo: 'Modelo',
  tituloPlural: 'Modelos',
  singular: 'modelo',
  plural: 'modelos',
};

export const AVATAR: PalabraDeModelo = {
  titulo: 'Avatar',
  tituloPlural: 'Avatares',
  singular: 'avatar',
  plural: 'avatares',
};

/**
 * **El nombre del MODO**, que NO sigue al rubro: la card de la home y de `/modos`, el
 * ítem de la sidebar, el breadcrumb, el sheet de Generar y el nodo del canvas de Flujos
 * dicen **"Modelo"** para todas las marcas
 * (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
 *
 * Es un objeto propio y no un alias de `MODELO` aunque hoy valgan lo mismo: son dos
 * preguntas distintas —"¿cómo se llama este modo?" contra "¿cómo le decimos a la
 * persona de la foto en ESTA marca?"— y el día que una se mueva no tiene que arrastrar
 * a la otra. Mismo criterio que los predicados de `rubro.ts`, que tampoco se aliasean
 * entre sí.
 *
 * Las formas en minúscula son para los shells viejos, que nombran al modo adentro de
 * una frase ("Imagen con modelo").
 */
export const MODO_MODELO: PalabraDeModelo = {
  titulo: 'Modelo',
  tituloPlural: 'Modelos',
  singular: 'modelo',
  plural: 'modelos',
};

/**
 * La palabra para un rubro dado. Comparación exacta contra el valor del select, igual
 * que todos los gates de `rubro.ts`: un rubro vacío, escrito a mano o todavía sin
 * resolver cae en **Avatar**, que es el término general. Es la decisión conservadora —
 * "Modelo" es el término de moda y sólo lo ve quien es de moda.
 */
export const palabraDeModelo = (rubro?: string | null): PalabraDeModelo =>
  esRubroModa(rubro) ? MODELO : AVATAR;

/**
 * La palabra de la marca activa AHORA, sin hooks y sin consultar nada: sale del rubro
 * que `useRubroMarcaActiva` deja cacheado en `localStorage`.
 *
 * Es para los módulos que arman copy fuera de React —las listas del menú del chat, la
 * ayuda de campos que lee el agente, los títulos de las galerías de guardados— y para
 * los `get etiqueta()` de esas tablas, donde no hay dónde colgar un hook. En un render,
 * usar `usePalabraDeModelo`: ése además re-dibuja cuando la marca cambia.
 *
 * El valor queda memorizado y se invalida con los mismos dos eventos que mira el hook,
 * así una tabla que lo lee en cada fila no pega contra `localStorage` una vez por fila.
 */
let memo: PalabraDeModelo | null = null;

const olvidar = () => {
  memo = null;
};

if (typeof window !== 'undefined') {
  window.addEventListener('atlas:brand-changed', olvidar);
  window.addEventListener('atlas:rubro-marca', olvidar);
}

export const palabraDeModeloActual = (): PalabraDeModelo =>
  (memo ??= palabraDeModelo(rubroMarcaCacheado()));

/**
 * Suscripción a los dos eventos que pueden cambiar la palabra: el cambio de marca
 * activa y el refresco del rubro cacheado que hace `useRubroMarcaActiva` al resolver su
 * consulta (`atlas:rubro-marca`). El segundo es el que importa en el primer arranque de
 * una cuenta: hasta que esa consulta vuelve, el cache está vacío y la palabra es la
 * general.
 *
 * El `olvidar()` de acá adentro no sobra aunque el módulo ya escuche los dos eventos:
 * garantiza que el memo esté frío ANTES de que React pida el snapshot, sin depender del
 * orden en que se registraron los oyentes.
 */
function suscribir(avisar: () => void): () => void {
  const alCambiar = () => {
    olvidar();
    avisar();
  };
  window.addEventListener('atlas:brand-changed', alCambiar);
  window.addEventListener('atlas:rubro-marca', alCambiar);
  return () => {
    window.removeEventListener('atlas:brand-changed', alCambiar);
    window.removeEventListener('atlas:rubro-marca', alCambiar);
  };
}

/**
 * La palabra de la marca activa, en un render.
 *
 * `useSyncExternalStore` y no `useRubroMarcaActiva`: este hook lo van a llamar muchos
 * componentes de la misma pantalla (el panel, el picker, el sheet y el chat), y ese
 * hook dispara DOS consultas a Supabase por montaje. Acá se lee el cache, que ya
 * mantiene fresco quien sí hace la consulta (el shell y la home), y el snapshot es uno
 * de los dos objetos constantes de arriba, así que su identidad no cambia entre renders.
 */
export const usePalabraDeModelo = (): PalabraDeModelo =>
  useSyncExternalStore(suscribir, palabraDeModeloActual, palabraDeModeloActual);
